import {
  ERROR_LOG_SIZE,
  ErrorLog,
  describeThrown,
  isChunkLoadError,
  type ErrorContext,
  type ErrorLogEntry,
  type ErrorLogKind,
} from '@supply/contracts'
import { toast } from 'sonner'

/**
 * Captura de erros do navegador inteiro.
 *
 * - `errorLog`: os últimos 30 erros (window.onerror, promessa rejeitada sem
 *   tratamento, console.error/warn, script que não baixou, boundary), com
 *   horário — vão no chamado. Quem grava os do navegador é o script do
 *   `<head>` do `index.html`, desde antes do bundle; aqui o app adota o mesmo
 *   buffer (`window.__supplyErrors`) e só soma o que é dele (tela, chunk).
 * - Erro que NÃO quebrou a tela (handler de clique, promessa solta) vira toast
 *   discreto com "Reportar". O que quebrou a tela é do `ErrorBoundary`.
 * - Chunk que sumiu depois de deploy: recarrega a página UMA vez
 *   (`sessionStorage`); se falhar de novo, a tela de erro aparece.
 */

interface SharedErrorBuffer {
  entries: ErrorLogEntry[]
  push: (entry: ErrorLogEntry) => void
}

const shared =
  typeof window === 'undefined' ? undefined : (window as Window & { __supplyErrors?: SharedErrorBuffer }).__supplyErrors

export const errorLog = new ErrorLog(ERROR_LOG_SIZE, shared?.entries)
// Daqui em diante o script do index.html grava pela regra do contrato.
if (shared) shared.push = (entry) => errorLog.push(entry)
/** Sem o script do index.html (teste, HTML antigo em cache), o app captura sozinho. */
const capturedByIndex = Boolean(shared)

let build: string | null = import.meta.env.DEV ? 'dev (Vite)' : null
let fatalShown = 0
let installed = false

export function logError(kind: ErrorLogKind, value: unknown): void {
  const { message, stack } = describeThrown(value)
  errorLog.push({ at: new Date().toISOString(), kind, message, ...(stack ? { stack } : {}) })
}

/** Retrato do momento do erro, para a tela e para o chamado. */
export function buildErrorContext(value: unknown, componentStack?: string | null): ErrorContext {
  const { message, stack } = describeThrown(value)
  return {
    message,
    ...(stack ? { stack } : {}),
    ...(componentStack ? { componentStack } : {}),
    route: `${window.location.pathname}${window.location.search}`,
    at: new Date().toISOString(),
    userAgent: navigator.userAgent,
    build,
    log: errorLog.list(),
  }
}

/** Boundary mostrando a tela de erro: os toasts de segundo plano se calam. */
export function setFatalShown(shown: boolean): void {
  fatalShown = Math.max(0, fatalShown + (shown ? 1 : -1))
}

// ---- recarga por chunk ------------------------------------------------------

const RELOAD_KEY = 'supply.recarga-por-versao'
const RELOAD_WINDOW_MS = 60_000

/**
 * Recarrega a página para pegar a versão nova. Devolve `false` (não recarrega)
 * se já recarregou há menos de um minuto: aí a falha é real e vai para a tela.
 */
export function reloadForNewVersionOnce(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0)
    if (Date.now() - last < RELOAD_WINDOW_MS) return false
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  } catch {
    // sem sessionStorage não há como evitar o loop: não recarrega
    return false
  }
  window.location.reload()
  return true
}

// ---- pedido de chamado ------------------------------------------------------

type ReportListener = (ctx: ErrorContext) => void
const reportListeners = new Set<ReportListener>()

/** O `ErrorReportHost` escuta e abre o chamado pré-preenchido. */
export function onErrorReportRequest(listener: ReportListener): () => void {
  reportListeners.add(listener)
  return () => reportListeners.delete(listener)
}

function requestErrorReport(ctx: ErrorContext): void {
  for (const listener of reportListeners) listener(ctx)
}

// ---- erros de segundo plano -------------------------------------------------

const QUIET_MS = 30_000
const lastToastAt = new Map<string, number>()

/** Ruído conhecido do navegador: entra no buffer, mas não incomoda ninguém. */
function isNoise(message: string): boolean {
  return /ResizeObserver loop|^Script error\.?$/i.test(message)
}

function notifyBackground(value: unknown): void {
  const ctx = buildErrorContext(value)
  if (isNoise(ctx.message)) return
  // Espera o React terminar: se o mesmo erro derrubou a tela, o boundary fala.
  window.setTimeout(() => {
    if (fatalShown > 0) return
    const now = Date.now()
    if (now - (lastToastAt.get(ctx.message) ?? 0) < QUIET_MS) return
    lastToastAt.set(ctx.message, now)
    toast('Algo falhou em segundo plano', {
      description: ctx.message.length > 140 ? `${ctx.message.slice(0, 139)}…` : ctx.message,
      duration: 8000,
      ...(reportListeners.size > 0
        ? { action: { label: 'Reportar', onClick: () => requestErrorReport({ ...ctx, log: errorLog.list() }) } }
        : {}),
    })
  }, 0)
}

function handleChunk(value: unknown): boolean {
  if (!isChunkLoadError(value)) return false
  if (!capturedByIndex) logError('carregamento', value)
  if (reloadForNewVersionOnce()) return true
  notifyBackground(value)
  return true
}

/** Liga a captura uma vez, antes de o React montar. */
export function installErrorCapture(): void {
  if (installed) return
  installed = true

  window.addEventListener('error', (event) => {
    // Recurso (img/script) que não carregou não tem `error`; só o JS interessa.
    const value = event.error ?? event.message
    if (!value) return
    if (handleChunk(value)) return
    if (!capturedByIndex) logError('erro', value)
    notifyBackground(value)
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (handleChunk(event.reason)) return
    if (!capturedByIndex) logError('promessa', event.reason)
    notifyBackground(event.reason)
  })

  // Vite avisa quando o preload de um chunk falha (deploy novo no ar).
  window.addEventListener('vite:preloadError', (event) => {
    const payload = (event as Event & { payload?: unknown }).payload
    logError('carregamento', payload ?? 'Falha ao carregar parte do sistema')
    if (reloadForNewVersionOnce()) event.preventDefault()
  })

  if (!capturedByIndex) captureConsoleError()

  // Em produção, a imagem publica o commit em /BUILD_COMMIT.txt (Dockerfile).
  if (!import.meta.env.DEV) {
    void fetch('/BUILD_COMMIT.txt', { cache: 'no-store' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((text) => {
        const commit = /^commit=(\S+)/m.exec(text)?.[1]
        if (commit) build = commit
      })
      .catch(() => undefined)
  }
}

function captureConsoleError(): void {
  const original = console.error.bind(console)
  console.error = (...args: unknown[]) => {
    try {
      const first = args.find((a) => a instanceof Error)
      const text = args
        .map((a) => (typeof a === 'string' ? a.replace(/%[osdifcO]/g, '').trim() : describeThrown(a).message))
        .filter(Boolean)
        .join(' ')
      // O React repete no console o erro que um boundary pegou; ele já entrou como "tela".
      if (!/error occurred in the <\w+> component/.test(text)) {
        errorLog.push({
          at: new Date().toISOString(),
          kind: 'console',
          message: text,
          ...(first instanceof Error && first.stack ? { stack: first.stack } : {}),
        })
      }
    } catch {
      // o buffer nunca derruba o console
    }
    original(...args)
  }
}
